<script setup lang="ts">
import { ref, watch } from "vue";
import { ComponentDefinition } from "@/shared/components/component-definition.model";
import { QDialogProps } from "quasar";
import { useScreenSize } from "@/shared/screen/composables/screen-size";
import PanelHeaderToolbar from "@/shared/components/side-panel/panel-header-toolbar.vue";

/**
 * Modal side panel variant.
 *
 * Use the dialog when content should be shown as a focused, temporary overlay with a backdrop. *
 */
const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        position?: QDialogProps["position"];
        title?: string;
        width?: number;
        icon?: string;
        titleComponent?: ComponentDefinition;
        testIdPrefix?: string;
    }>(),
    {
        position: "left",
        width: 400,
    }
);

const emit = defineEmits<{
    (e: "update:modelValue", visible: boolean): void;
    (e: "onLoadMore", moreElements: (hasMoreElements: boolean) => void): void;
}>();

const popupVisible = ref(props.modelValue);

const { isXSmall } = useScreenSize();

watch(
    () => props.modelValue,
    () => {
        popupVisible.value = props.modelValue;
    }
);
watch(popupVisible, () => {
    emit("update:modelValue", popupVisible.value);
});

function closeDialog() {
    popupVisible.value = false;
}
</script>

<template>
    <q-dialog v-model="popupVisible" :position="position" square no-shake @hide="closeDialog" :maximized="isXSmall">
        <q-card class="column" :style="`width: ${width}px; height: 100%;`">
            <q-card-section class="col-auto q-pa-xs">
                <component v-if="titleComponent" :is="titleComponent?.getComponent()" />
                <PanelHeaderToolbar
                    v-else
                    :title="title ?? ''"
                    :icon="icon"
                    :test-id-prefix="testIdPrefix"
                    @close="closeDialog"
                />
            </q-card-section>
            <q-separator />
            <div class="col fit">
                <slot />
            </div>
            <q-card-actions v-if="$slots.footer" class="col-auto" vertical align="center">
                <slot name="footer" />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>
